<script lang="ts">
	import type { QueryStore } from '$houdini';
	import type { InputControl } from '$lib/components/atoms/input/types';
	import type { IconName } from '$lib/components/icons/types';
	import type { Result } from '$lib/components/search/types';
	import type { Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import type { UserAction } from '../map/types';
	import type { AutocompleteLabel } from './types';
	import AutocompleteLine from '$lib/components/search/autocomplete/AutocompleteLine.svelte';
	import ErrorMessage from '$lib/components/search/autocomplete/ErrorMessage.svelte';
	import HistoryResult from '$lib/components/search/autocomplete/HistoryResult.svelte';
	import Line from '$lib/components/search/autocomplete/Line.svelte';
	import AutocompleteSkeleton from '$lib/components/search/autocomplete/Skeleton.svelte';
	import { isMyLocation } from '$lib/components/search/itinerary/myLocResult';
	import SearchInput from '$lib/components/search/SearchInput.svelte';
	import { createSearchHistoryStore } from '$lib/services/store/searchHistory';
	import {
		buildSearchbarUniquePayload,
		trackSearchInputClick,
		trackSearchInputFocus,
		trackSearchInputResults,
		trackSearchInputSearch,
	} from '$lib/services/tracking/googleAnalytics/searchbar';
	import { generateRandomWords } from '$lib/utils/generateRandomWords';
	import * as m from '$paraglide/messages';
	import { debounce } from 'throttle-debounce';

	const DEBOUNCE_TIME = 500;
	const SHOW_LOADING_DELAY = 500;
	const MIN_LOADING_VISIBLE = 500;
	const START_FETCHING_AT_N_CHARACTER = 3;
	const DEFAULT_SKELETON_LINECOUNT = 5;
	const LENGTH_MIN_REQUEST_API = 3;

	type QueryData = $$Generic<Record<string, any>>;
	type V = $$Generic<Record<string, any>>;
	type SearchQueryStore = QueryStore<QueryData, V>;
	type Results = Result[];
	type GetResultsFunction = $$Generic<(queryData: QueryData) => Results>;

	interface Props {
		placeholder?: string;
		ariaLabel?: string;
		id?: string;
		disabled?: boolean;
		queryStore?: SearchQueryStore;
		getStoreResults: GetResultsFunction;
		icon?: IconName;
		controls?: InputControl[];
		defaultResults?: Results | null;
		label?: string;
		hasTrackingEnabled?: boolean;
		class?: ClassValue;
		initialValue?: string;
		hasHistoryEnabled?: boolean;
		forceOpenInput?: boolean;
		queryToVariables: (q: string) => V;
		resultToLabel: (r: Result) => AutocompleteLabel;
		onclick?: () => void;
		onSelect?: (params: {
			result: Result | null;
			userAction: UserAction | null;
			isFromStore: boolean;
			results: Results | null;
			searchId: string | undefined;
		}) => void;
		onActivate?: () => void;
		onDeactivate?: () => void;
		beforeListItems?: Snippet<[onmouseover: (index: number) => void, activeItemIndex: number, value: string]>;
	}

	const {
		defaultResults = null,
		hasTrackingEnabled = false,
		initialValue = '',
		hasHistoryEnabled = false,
		placeholder,
		ariaLabel,
		id,
		disabled,
		queryStore,
		queryToVariables,
		resultToLabel,
		getStoreResults,
		icon,
		controls,
		label,
		class: classes,
		onclick,
		onSelect,
		onActivate,
		onDeactivate,
		beforeListItems,
		forceOpenInput = false,
	}: Props = $props();

	let inputState: 'initial' | 'searching' = $state('initial');
	let isLoading = $state(false);
	let input: SearchInput | undefined = $state();
	let searchId = generateSearchId();
	const searchHistoryStore = hasHistoryEnabled ? createSearchHistoryStore() : undefined;
	const data = $derived($queryStore?.data);
	// State whether the search input is reset without any user input or if the user has typed something
	const errors = $derived($queryStore?.errors ?? null);
	const hasError = $derived(errors !== null && errors.length > 0);
	const hasMyLocation = $derived.by(() => defaultResults?.length === 1 && isMyLocation(defaultResults[0]) && !input?.getValue());
	let loadTimer: ReturnType<typeof setTimeout> | undefined;
	let hideLoadTimer: ReturnType<typeof setTimeout> | undefined;
	// timestamp when loading flag has been enabled
	let loadingTS: number | undefined;
	const results: Results | null = $derived.by(() => {
		const history = $searchHistoryStore ?? [];

		if (inputState === 'initial') {
			return orderSearchResults([...history]);
		}

		if (data) {
			return orderSearchResults([...history, ...(getStoreResults(data) ?? [])]);
		}

		return orderSearchResults([...history]);
	});

	const needToDisplayedBffResults = $derived(hasMyLocation || (input?.getValue() && input?.getValue()?.length >= LENGTH_MIN_REQUEST_API));
	const shouldDisplayList = $derived(isLoading || results?.length > 0 || hasError || Boolean(needToDisplayedBffResults) || forceOpenInput);
	const skeletonTexts = $derived.by(() => {
		if (!isLoading) {
			return [];
		}

		if (results && results.length > DEFAULT_SKELETON_LINECOUNT) {
			return results.map(result => resultToLabel(result).label);
		}

		return Array.from({ length: DEFAULT_SKELETON_LINECOUNT }, () => {
			return generateRandomWords({
				minWords: 5,
				maxWords: 10,
				minWordLength: 2,
				maxWordLength: 15,
			});
		});
	});

	const forceOpen = $derived.by(() => {
		if (input) {
			const hasNoResultWithInputValue = !results?.length && !isLoading && input?.getValue()?.length > 0;
			return (isLoading || hasError || shouldDisplayList || hasNoResultWithInputValue);
		}
		return false;
	});

	function orderSearchResults(results: Results) {
		const resultsToOrder = hasMyLocation ? results.reverse() : results;
		return resultsToOrder.flat();
	}

	function generateSearchId() {
		return globalThis.crypto.randomUUID();
	}

	export function clear() {
		input?.clear?.();
		searchId = generateSearchId();
	}

	function resetLoading() {
		isLoading = false;
		loadingTS = undefined;
		if (loadTimer) {
			clearTimeout(loadTimer);
			loadTimer = undefined;
		}
		if (hideLoadTimer) {
			clearTimeout(hideLoadTimer);
			hideLoadTimer = undefined;
		}
	}
	function startLoading() {
		// make sure we are in initial inputState
		resetLoading();
		loadTimer = setTimeout(() => {
			isLoading = true;
			loadingTS = Date.now();
		}, SHOW_LOADING_DELAY);
	}

	function endLoading() {
		const delay = loadingTS ? loadingTS + MIN_LOADING_VISIBLE - Date.now() : 0;
		if (delay > 0) {
			hideLoadTimer = setTimeout(() => resetLoading(), delay);
		} else {
			resetLoading();
		}
	}
	async function fetchResults(q: string) {
		if (q.length < START_FETCHING_AT_N_CHARACTER) {
			inputState = 'initial';
			return;
		}
		if (q.length === 0) {
			searchId = generateSearchId();
			resetLoading();
			inputState = 'initial';
			return;
		}

		if (hasTrackingEnabled) {
			trackSearchInputSearch(buildSearchbarUniquePayload({ inputValue: q, searchId }));
		}
		startLoading();
		const result = await queryStore?.fetch({ variables: queryToVariables(q), blocking: true });
		const resultDatas = result?.data;

		if (hasTrackingEnabled) {
			const resultsArray = resultDatas ? getStoreResults(resultDatas) : [];
			trackSearchInputResults(
				buildSearchbarUniquePayload({
					inputValue: q,
					resultsLength: resultsArray?.length ?? 0,
					results: resultsArray,
					searchId,
				}),
			);
		}

		inputState = 'searching';
		endLoading();
	}

	function select(result: Result | null, userAction: UserAction | null, isFromStore = false) {
		// result and userAction are null when clearing the input
		if (!result && !userAction) {
			searchId = generateSearchId();
		}

		if (result !== null) {
			// If the user selected an item, prepopulate the data for the next search
			queryStore?.fetch({ variables: queryToVariables(resultToLabel(result).label) });
		}

		if (result === null) {
			// If the user cleared the input, reset the inputState
			inputState = 'initial';
		}

		if (result) {
			searchHistoryStore?.add(result);
		}

		if (hasTrackingEnabled) {
			const resultsList = isFromStore && !results ? $searchHistoryStore : results;
			const payload = buildSearchbarUniquePayload({
				searchId,
				isFromStore,
				selected: result,
				results: resultsList,
				inputValue: input?.getValue(),
				resultsLength: results?.length,
			});
			trackSearchInputClick(payload);
		}

		onSelect?.({ result, userAction, isFromStore, results, searchId });
	}

	function deactivate() {
		// If the user left the input, reset the inputState so they don't see suggestions after they come back
		inputState = 'initial';
		onDeactivate?.();
	}

	function activate() {
		trackSearchInputFocus(buildSearchbarUniquePayload({ inputValue: input?.getValue(), searchId }));
		searchHistoryStore?.query(input?.getValue() ?? '');
		onActivate?.();
	}

	const debouncedFetchResults = debounce(DEBOUNCE_TIME, fetchResults);

	function handleClearInput() {
		searchId = generateSearchId();
	}

	function isFromStorage(index = 0, hasMyLoc = false) {
		const list = $searchHistoryStore ?? [];
		const start = hasMyLoc && results?.length === 0 ? 1 : 0;
		const end = list.length + start;

		return index >= start && index < end;
	}

	$effect(() => {
		if (typeof input?.getValue() !== 'undefined') {
			searchHistoryStore?.query(input?.getValue());
		}
	});
</script>

<SearchInput
	{resultToLabel}
	{id}
	{icon}
	{results}
	{ariaLabel}
	{placeholder}
	{initialValue}
	{controls}
	{defaultResults}
	{label}
	{onclick}
	{forceOpen}
	{beforeListItems}
	bind:this={input}
	disabled={disabled || !queryStore}
	class={classes}
	onSelect={(result, userAction, isFromStore) => {
		select(result, userAction, isFromStore);
	}}
	onActivate={activate}
	onDeactivate={deactivate}
	onSearch={val => debouncedFetchResults(val)}
	onClearInput={handleClearInput}
>
	{#snippet resultItem({ result, resultIndex, isOpen, activeItemIndex, handleSelectItem, value, id, onmouseover })}
		{@const isFromStore = isFromStorage(resultIndex, hasMyLocation)}
		<AutocompleteLine
			{id}
			testid={isFromStore ? 'history' : 'result'}
			label={resultToLabel(result).label}
			highlighted={value}
			additionalLabel={resultToLabel(result).additionalLabel}
			active={resultIndex === activeItemIndex}
			isVisible={!isOpen}
			onSelectItem={() => handleSelectItem(result, false)}
			{onmouseover}
		>
			{#snippet lineItem({ beforeText, afterText, highlightText, additionalLabel, onSelectItem })}
				{#if isFromStore}
					<HistoryResult
						{beforeText}
						{afterText}
						{highlightText}
						{additionalLabel}
						onSelectItemStored={() => handleSelectItem(result, true)}
						onRemoveItemStored={() => searchHistoryStore?.remove(result)}
					/>
				{:else if !isLoading}
					<Line {beforeText} {afterText} {highlightText} {additionalLabel} {onSelectItem} />
				{/if}
			{/snippet}
		</AutocompleteLine>
	{/snippet}
	{#if isLoading}
		{#each skeletonTexts as text, index (index)}
			<AutocompleteSkeleton {text} isOpen={forceOpen} />
		{/each}
	{:else if hasError || (!results?.length && !isLoading && input?.getValue()?.length > 0)}
		<ErrorMessage message={hasError ? m.search_tools__messages__error() : m.search_tools__messages__empty()} isOpen={forceOpen} />
	{/if}
</SearchInput>
